import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
import { useBranding } from '@/lib/branding/BrandingProvider';

export type InviteRole = 'admin' | 'team_lead' | 'recruiter' | 'viewer';

export interface InviteEmployeeInput {
  email: string;
  full_name: string;
  role: InviteRole;
}

function generateInviteToken(): string {
  const buf = new Uint8Array(32);
  crypto.getRandomValues(buf);
  return Array.from(buf, (b) => b.toString(16).padStart(2, '0')).join('');
}

export function useInviteEmployee() {
  const qc = useQueryClient();
  const { companyId, name } = useBranding();

  return useMutation({
    mutationFn: async (input: InviteEmployeeInput) => {
      const email = input.email.trim().toLowerCase();

      const { data: existing, error: dupErr } = await supabase
        .from('b2b_employees')
        .select('id')
        .eq('b2b_company_id', companyId)
        .eq('email', email)
        .maybeSingle();

      if (dupErr) throw new Error(`b2b_employees: ${dupErr.message}`);
      if (existing) {
        throw new Error('A teammate with this email already exists.');
      }

      const inviteToken = generateInviteToken();
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7);

      const { data: created, error: insertErr } = await supabase
        .from('b2b_employees')
        .insert({
          b2b_company_id: companyId,
          email,
          full_name: input.full_name.trim(),
          role: input.role,
          is_active: true,
          invite_token: inviteToken,
          invite_status: 'pending',
          invite_expires_at: expiresAt.toISOString(),
        })
        .select()
        .single();

      if (insertErr || !created) {
        throw new Error(`b2b_employees: ${insertErr?.message ?? 'insert failed'}`);
      }

      const { error: emailErr } = await supabase.functions.invoke('send-b2b-invite', {
        body: {
          employee_id: created.id,
          email,
          full_name: input.full_name.trim(),
          company_name: name,
          invite_token: inviteToken,
          role: input.role,
        },
      });

      if (emailErr) {
        await supabase.from('b2b_employees').delete().eq('id', created.id);
        throw new Error(emailErr.message ?? 'Failed to send invite email');
      }

      return created;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['neurax', 'employees'] });
    },
  });
}
